import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useEffect, useState } from "react";
import {
  Button,
  Form,
  InputGroup,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import { formatFileSize } from "helpers/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExternalLinkAlt,
  faLink,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { format } from "date-fns";

type FileInfo = {
  nome: string;
  tamanho: number;
  ultimaModificacao: string;
  link: string;
};

type VersaoUrlSelectorProps = {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  isInvalid?: boolean;
  errorMessage?: string;
  name: string; // Nome do campo para integração com react-hook-form
  register: any; // Função register do react-hook-form
};

const fetchAtualizacoes = async () => {
  const { data } = await axios.get<FileInfo[]>(
    "/api/hubs/cliente/atualizacoes"
  );
  return data;
};

const VersaoUrlSelector = ({
  label,
  placeholder,
  value,
  onChange,
  isInvalid,
  errorMessage,
  name,
  register,
}: VersaoUrlSelectorProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const { data: files, isLoading } = useQuery({
    queryKey: ["atualizacoes"],
    queryFn: fetchAtualizacoes,
    enabled: isModalOpen,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const filteredFiles = files?.filter((file) =>
    file.nome.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelect = (url: string) => {
    onChange(url);
    setIsModalOpen(false);
  };

  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label>{label}</Form.Label>
        <InputGroup>
          <Form.Control
            type="text"
            placeholder={placeholder}
            {...register(name, { required: true })}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            isInvalid={isInvalid}
          />
          <Button
            variant="outline-secondary"
            onClick={() => setIsModalOpen(true)}
          >
            <FontAwesomeIcon icon={faLink} className="me-2" />
            Selecionar
          </Button>
          <Form.Control.Feedback type="invalid">
            {errorMessage}
          </Form.Control.Feedback>
        </InputGroup>
      </Form.Group>

      <Modal
        show={isModalOpen}
        onHide={() => setIsModalOpen(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Selecionar URL</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <InputGroup>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Buscar por nome do arquivo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </div>

          {isLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Carregando...</span>
              </Spinner>
            </div>
          ) : (
            <div className="table-responsive" style={{ maxHeight: "400px" }}>
              <Table hover>
                <thead className="sticky-top bg-body">
                  <tr>
                    <th>Nome</th>
                    <th>Tamanho</th>
                    <th>Última Modificação</th>
                    <th className="text-end">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredFiles && filteredFiles.length > 0 ? (
                    filteredFiles.map((file) => (
                      <tr key={file.link}>
                        <td className="text-break">
                          <span
                            className="d-inline-block text-truncate"
                            style={{ maxWidth: "300px" }}
                          >
                            {file.nome}
                          </span>
                        </td>
                        <td>{formatFileSize(file.tamanho)}</td>
                        <td>
                          {format(new Date(file.ultimaModificacao), 'dd/MM/yyyy HH:mm')}
                        </td>
                        <td className="text-end">
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => window.open(file.link, "_blank")}
                            className="me-2 p-0 text-body fs-9"
                          >
                            <FontAwesomeIcon icon={faExternalLinkAlt} />
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleSelect(file.link)}
                          >
                            Selecionar
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="text-center py-4">
                        {searchTerm
                          ? "Nenhum arquivo encontrado com esse termo de busca."
                          : "Nenhum arquivo disponível."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="link"
            className="text-body"
            onClick={() => setIsModalOpen(false)}
          >
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default VersaoUrlSelector;
